import React, { useState, useEffect, useCallback } from 'react';
import './EventManagement.css';
import EventForm from './EventForm';
import EventAnalytics from './EventAnalytics';
import LicensePanel from './LicensePanel';
import SubadminPanel from './SubadminPanel';
import { SupportManagement, isSupportEnabled } from '../../support';

const LICENSE_ENABLED = process.env.REACT_APP_LICENSE_ENABLED === 'true';
import {
  getSigner,
  getProvider,
  getAddresses,
  eventTicketWithEvents,
  createOnChainEvent,
  updateOnChainEvent,
  setOnChainEventActive,
  addOnChainTicketType,
  getOnChainTicketType,
} from '../../services/contracts';
import { getEvents as getSubgraphEvents } from '../../services/subgraph';

// Fallback images for missing event images
const FALLBACK_IMAGES = ['/events/pregame.png', '/events/basefest.png', '/events/test.png'];
const getDefaultImage = (eventId) => {
  const hash = String(eventId || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return FALLBACK_IMAGES[hash % FALLBACK_IMAGES.length];
};

const CONTRACT_ADDRESS = process.env.REACT_APP_EVENT_TICKET_WITH_EVENTS_ADDRESS ||
  process.env.REACT_APP_EVENT_TICKET_ADDRESS || '';
const CHAIN_ID = Number(process.env.REACT_APP_CHAIN_ID || 84532);

// localStorage supplement for fields not stored on-chain (capacity, category, visibility, venueCoordinates)
const LOCAL_SUPPLEMENT_KEY = 'admin_events_supplement';
const loadSupplement = () => {
  try { return JSON.parse(localStorage.getItem(LOCAL_SUPPLEMENT_KEY) || '{}'); } catch { return {}; }
};
const saveSupplement = (data) => {
  try { localStorage.setItem(LOCAL_SUPPLEMENT_KEY, JSON.stringify(data)); } catch {}
};

const EventManagement = () => {
  const [events, setEvents] = useState([]);
  const [eventStats, setEventStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [view, setView] = useState('list');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // ---------- Load events from subgraph + on-chain fallback ----------

  const loadEventsFromSubgraph = async () => {
    try {
      const sgEvents = await getSubgraphEvents({ first: 200, activeOnly: false });
      return sgEvents.map(e => ({
        id: e.eventId || e.id,
        eventId: Number(e.eventId),
        name: e.name,
        description: e.description || '',
        location: e.location,
        image: e.imageURI || '',
        date: new Date(Number(e.startTime) * 1000).toISOString(),
        startTime: Number(e.startTime),
        endTime: Number(e.endTime),
        organizer: e.organizer,
        active: e.active,
        contractAddress: CONTRACT_ADDRESS,
        chainId: CHAIN_ID,
        totalMints: Number(e.totalMints || 0),
        totalRevenue: Number(e.totalRevenue || 0),
        ticketTypes: (e.ticketTypes || []).map(tt => ({
          id: Number(tt.id || tt.eventId),
          name: tt.name,
          price: Number(tt.price),
          maxSupply: Number(tt.maxSupply),
          totalMinted: Number(tt.totalMinted || 0),
          saleStart: Number(tt.saleStart || 0),
          saleEnd: Number(tt.saleEnd || 0),
        })),
        _source: 'subgraph',
      }));
    } catch (err) {
      console.warn('[EventManagement] Subgraph unavailable:', err.message);
      return null;
    }
  };

  const loadEventsFromContract = async () => {
    try {
      const contract = eventTicketWithEvents();
      const ids = await contract.getAllEventIds();
      const events = [];
      for (const id of ids) {
        try {
          const result = await contract.getEvent(Number(id));
          // Load ticket types for this event
          const ttIds = result[8] || [];
          const ticketTypes = [];
          for (const ttId of ttIds) {
            try {
              const tt = await contract.getTicketType(Number(ttId));
              ticketTypes.push({
                id: Number(ttId),
                name: tt[1] || '',
                price: Number(tt[2]),
                maxSupply: Number(tt[3]),
                totalMinted: Number(tt[4]),
                saleStart: Number(tt[6]),
                saleEnd: Number(tt[7]),
              });
            } catch { continue; }
          }
          events.push({
            id: Number(id),
            eventId: Number(id),
            name: result[0],
            description: result[1] || '',
            location: result[2],
            image: result[3] || '',
            date: new Date(Number(result[4]) * 1000).toISOString(),
            startTime: Number(result[4]),
            endTime: Number(result[5]),
            organizer: result[6],
            active: result[7],
            contractAddress: CONTRACT_ADDRESS,
            chainId: CHAIN_ID,
            ticketTypes,
            _source: 'contract',
          });
        } catch { continue; }
      }
      return events;
    } catch (err) {
      console.warn('[EventManagement] Contract read failed:', err.message);
      return null;
    }
  };

  const loadEventsFromJSON = async () => {
    try {
      const res = await fetch('/events.json');
      const data = await res.json();
      return (Array.isArray(data) ? data : []).map(e => ({ ...e, _source: 'json' }));
    } catch {
      return [];
    }
  };

  // Enrich events with localStorage supplement + on-chain details
  const enrichEvents = async (eventsList) => {
    const supplement = loadSupplement();
    const contract = eventTicketWithEvents();

    return Promise.all(eventsList.map(async (evt) => {
      // Apply localStorage supplement (includes saved IPFS image)
      const sup = supplement[String(evt.eventId)] || supplement[String(evt.id)] || {};
      let enriched = { ...evt };

      // Supplement image takes priority (user's uploaded IPFS image)
      if (sup.image) enriched.image = sup.image;
      // Apply other supplement fields (capacity, category, venue, etc.)
      if (sup.capacity) enriched.capacity = sup.capacity;
      if (sup.category) enriched.category = sup.category;
      if (sup.visibility) enriched.visibility = sup.visibility;
      if (sup.venueCoordinates) enriched.venueCoordinates = sup.venueCoordinates;
      if (sup.venue) enriched.venue = sup.venue;

      // If still missing image/description, fetch from contract
      if (!enriched.image || !enriched.description) {
        try {
          const result = await contract.getEvent(Number(evt.eventId));
          if (!enriched.description) enriched.description = result[1] || '';
          if (!enriched.image) enriched.image = result[3] || '';
        } catch (err) {
          console.warn(`[EventManagement] Failed to enrich event ${evt.eventId}:`, err.message);
        }
      }

      // Use imageURI as last fallback
      if (!enriched.image && enriched.imageURI) enriched.image = enriched.imageURI;

      return enriched;
    }));
  };

  const loadEvents = useCallback(async () => {
    setLoading(true);
    let eventsList = null;

    // 1. Try subgraph (fastest, indexed)
    const sgEvents = await loadEventsFromSubgraph();

    // 2. Also read from contract (catches events not yet indexed by subgraph)
    const contractEvents = await loadEventsFromContract();

    if (sgEvents && sgEvents.length > 0) {
      eventsList = sgEvents;
      // Merge in any contract-only events (newly created, not yet indexed)
      if (contractEvents && contractEvents.length > 0) {
        const sgIds = new Set(sgEvents.map(e => String(e.eventId)));
        const newEvents = contractEvents.filter(e => !sgIds.has(String(e.eventId)));
        if (newEvents.length > 0) {
          eventsList = [...eventsList, ...newEvents];
          console.log(`[EventManagement] Merged ${newEvents.length} contract-only events (not yet indexed)`);
        }
      }
    } else if (contractEvents && contractEvents.length > 0) {
      eventsList = contractEvents;
    } else {
      // 3. Fallback: static events.json
      eventsList = await loadEventsFromJSON();
    }

    // Enrich with on-chain details + localStorage supplement
    if (eventsList && eventsList.length > 0) {
      eventsList = await enrichEvents(eventsList);
    }

    console.log(`[EventManagement] Loaded ${eventsList?.length || 0} events`);
    setEvents(eventsList || []);
    computeStats(eventsList || []);
    setLoading(false);
  }, []);

  // ---------- Stats from subgraph/contract data ----------

  const computeStats = (eventsList) => {
    const stats = {};
    for (const event of eventsList) {
      const tts = event.ticketTypes || [];
      const totalCapacity = tts.reduce((s, tt) => s + (Number(tt.maxSupply) || 0), 0) || Number(event.capacity) || 0;
      const ticketsSold = event.totalMints || tts.reduce((s, tt) => s + (Number(tt.totalMinted) || 0), 0);
      const revenue = event.totalRevenue
        ? (Number(event.totalRevenue) / 100).toFixed(2)
        : tts.reduce((s, tt) => s + (Number(tt.totalMinted) || 0) * (Number(tt.price) || 0) / 100, 0).toFixed(2);
      stats[event.id] = {
        ticketsSold,
        revenue,
        totalCapacity,
        available: Math.max(0, totalCapacity - ticketsSold),
        percentSold: totalCapacity > 0 ? ((ticketsSold / totalCapacity) * 100).toFixed(2) : '0.00',
        attendees: 0,
      };
    }
    setEventStats(stats);
  };

  useEffect(() => { loadEvents(); }, [loadEvents]);

  // ---------- Event handlers ----------

  const handleCreateEvent = () => { setSelectedEvent(null); setView('create'); };

  // Fetch full on-chain data before editing (description, imageURI, ticketTypes)
  const handleEditEvent = async (event) => {
    const contract = eventTicketWithEvents();
    const supplement = loadSupplement();
    const sup = supplement[event.id] || supplement[event.eventId] || {};
    let fullEvent = { ...event, ...sup };

    // Fetch full event details from contract
    if (event.eventId) {
      try {
        const result = await contract.getEvent(Number(event.eventId));
        fullEvent.description = result[1] || fullEvent.description || '';
        fullEvent.image = sup.image || result[3] || fullEvent.image || '';
        fullEvent.venue = sup.venue || fullEvent.venue || result[2] || '';
        fullEvent.location = result[2] || fullEvent.location || '';

        // Fetch ticket types from contract
        const ttIds = result[8] || []; // ticketTypeIds array
        if (ttIds.length > 0) {
          const ticketTypes = [];
          for (const ttId of ttIds) {
            try {
              const tt = await contract.getTicketType(Number(ttId));
              ticketTypes.push({
                id: Number(ttId),
                name: tt[1] || '',
                price: (Number(tt[2]) / 100).toFixed(2), // PGT 2 decimals → display
                maxSupply: Number(tt[3]),
                sold: Number(tt[4]),
                available: Number(tt[5]),
                saleStart: Number(tt[6]),
                saleEnd: Number(tt[7]),
                metadataURI: tt[8] || '',
              });
            } catch { continue; }
          }
          if (ticketTypes.length > 0) fullEvent.ticketTypes = ticketTypes;
        }
      } catch (err) {
        console.warn('[EventManagement] Failed to fetch full event for editing:', err.message);
      }
    }

    setSelectedEvent(fullEvent);
    setView('edit');
  };

  const handleViewAnalytics = (event) => { setSelectedEvent(event); setView('analytics'); };
  const handleManageSubadmins = (event) => { setSelectedEvent(event); setView('subadmins'); };
  const handleBackToList = () => { setView('list'); setSelectedEvent(null); };

  // ---------- Save event (create/update) — direct on-chain ----------

  const handleSaveEvent = async (eventData) => {
    setSaving(true);
    setSaveStatus('Connecting wallet...');
    try {
      const signer = await getSigner();
      const signerAddress = await signer.getAddress();

      // Verify wallet is on the correct chain
      const provider = signer.provider || await getProvider();
      const network = await provider.getNetwork();
      const expectedChainId = Number(process.env.REACT_APP_CHAIN_ID || 84532);
      if (Number(network.chainId) !== expectedChainId) {
        alert(`Wrong network! Please switch to chain ${expectedChainId}. Current: ${network.chainId}`);
        setSaving(false);
        setSaveStatus('');
        return;
      }

      const eventDate = new Date(eventData.date);
      const startTime = Math.floor(eventDate.getTime() / 1000);
      const endTime = eventData.endTime
        ? Math.floor(new Date(eventData.endTime).getTime() / 1000)
        : startTime + 4 * 60 * 60;
      const tags = eventData.tags || (eventData.category ? [eventData.category] : []);

      let txHash, eventId;
      const failedTicketTypes = [];

      if (selectedEvent && selectedEvent.eventId) {
        // Verify signer is the organizer
        try {
          const contract = eventTicketWithEvents();
          const onChain = await contract.getEvent(Number(selectedEvent.eventId));
          if (onChain[6] && onChain[6].toLowerCase() !== signerAddress.toLowerCase()) {
            alert(`Only the event organizer (${onChain[6]}) can edit this event.`);
            setSaving(false);
            setSaveStatus('');
            return;
          }
        } catch (err) {
          console.error('[EventManagement] Organizer check failed:', err.message);
          alert('Failed to verify organizer permission. Check your network connection and try again.');
          setSaving(false);
          setSaveStatus('');
          return;
        }

        setSaveStatus('Updating event on-chain...');
        const result = await updateOnChainEvent(signer, selectedEvent.eventId, {
          name: eventData.name,
          description: eventData.description || '',
          location: eventData.venue || eventData.location || '',
          imageURI: eventData.image || '',
          startTime,
          endTime,
          tags,
          metadataURI: eventData.metadataURI || '',
        });
        txHash = result.txHash;
        eventId = selectedEvent.eventId;

        // Add new ticket types that don't exist on-chain yet
        if (eventData.ticketTypes && eventData.ticketTypes.length > 0) {
          const existingTTIds = new Set((selectedEvent.ticketTypes || []).map(tt => Number(tt.id)));
          const newTicketTypes = eventData.ticketTypes.filter(tt => !existingTTIds.has(Number(tt.id)));
          if (newTicketTypes.length > 0) {
            setSaveStatus(`Adding ${newTicketTypes.length} new ticket type(s)...`);
            for (let i = 0; i < newTicketTypes.length; i++) {
              const tt = newTicketTypes[i];
              try {
                setSaveStatus(`Adding ticket type ${i + 1}/${newTicketTypes.length}: "${tt.name}"...`);
                await addOnChainTicketType(signer, eventId, {
                  ticketTypeId: Number(tt.id) || Date.now() + i,
                  name: tt.name || '',
                  price: Math.round(parseFloat(tt.price) * 100) || 0,
                  maxSupply: parseInt(tt.maxSupply) || 100,
                  saleStart: startTime,
                  saleEnd: endTime,
                });
                console.log(`[EventManagement] Ticket type "${tt.name}" added on-chain`);
              } catch (err) {
                failedTicketTypes.push(tt.name);
                console.error(`[EventManagement] Failed to add ticket type "${tt.name}":`, err);
              }
            }
          }
        }
      } else {
        // Create new event on-chain
        setSaveStatus('Creating event on-chain...');
        const result = await createOnChainEvent(signer, {
          name: eventData.name,
          description: eventData.description || '',
          location: eventData.venue || eventData.location || '',
          imageURI: eventData.image || '',
          startTime,
          endTime,
          tags,
          metadataURI: eventData.metadataURI || '',
        });
        txHash = result.txHash;
        eventId = result.eventId;

        // Add ticket types on-chain
        if (eventId && eventData.ticketTypes && eventData.ticketTypes.length > 0) {
          for (let i = 0; i < eventData.ticketTypes.length; i++) {
            const tt = eventData.ticketTypes[i];
            try {
              setSaveStatus(`Adding ticket type ${i + 1}/${eventData.ticketTypes.length}: "${tt.name}"...`);
              await addOnChainTicketType(signer, eventId, {
                ticketTypeId: Number(tt.id) || Date.now() + i,
                name: tt.name || '',
                price: Math.round(parseFloat(tt.price) * 100) || 0, // PGT has 2 decimals
                maxSupply: parseInt(tt.maxSupply) || 100,
                saleStart: startTime,
                saleEnd: endTime,
              });
              console.log(`[EventManagement] Ticket type "${tt.name}" added on-chain`);
            } catch (err) {
              failedTicketTypes.push(tt.name);
              console.error(`[EventManagement] Failed to add ticket type "${tt.name}":`, err);
            }
          }
        }
      }

      // Save supplementary data to localStorage (image + fields not indexed by subgraph)
      setSaveStatus('Saving local data...');
      const supplement = loadSupplement();
      supplement[String(eventId)] = {
        image: eventData.image || '',
        imageMetadata: eventData.imageMetadata || null,
        capacity: eventData.capacity,
        category: eventData.category,
        visibility: eventData.visibility,
        venueCoordinates: eventData.venueCoordinates,
        venue: eventData.venue,
        metadataURI: eventData.metadataURI || '',
      };
      saveSupplement(supplement);

      setSaveStatus('Reloading events...');
      await loadEvents();
      setView('list');
      setSelectedEvent(null);

      if (failedTicketTypes.length > 0) {
        alert(`Event saved on-chain (TX: ${txHash?.slice(0, 14)}...) but ${failedTicketTypes.length} ticket type(s) failed: ${failedTicketTypes.join(', ')}`);
      } else {
        alert(`Event ${selectedEvent ? 'updated' : 'created'} on-chain! TX: ${txHash?.slice(0, 14)}...`);
      }
    } catch (error) {
      console.error('Error saving event on-chain:', error);
      const msg = error.reason || error.message || 'Unknown error';
      alert(`Failed to save event: ${msg}`);
    } finally {
      setSaving(false);
      setSaveStatus('');
    }
  };

  // ---------- Delete (deactivate) — direct on-chain ----------

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('This will deactivate the event on-chain. Continue?')) return;
    try {
      const signer = await getSigner();
      const id = typeof eventId === 'object' ? eventId.eventId : eventId;
      const result = await setOnChainEventActive(signer, id, false);
      await loadEvents();
      alert(`Event deactivated on-chain. TX: ${result.txHash?.slice(0, 14)}...`);
    } catch (error) {
      console.error('Error deactivating event:', error);
      alert(`Failed to deactivate: ${error.reason || error.message}`);
    }
  };

  // ---------- Toggle active — direct on-chain ----------

  const handleToggleActive = async (event) => {
    try {
      const signer = await getSigner();
      const result = await setOnChainEventActive(signer, event.eventId, !event.active);
      await loadEvents();
      console.log(`[EventManagement] Event ${event.eventId} ${!event.active ? 'activated' : 'deactivated'} on-chain`);
    } catch (error) {
      console.error('Error toggling event:', error);
      alert(`Failed to toggle status: ${error.reason || error.message}`);
    }
  };

  // ---------- Filters ----------

  const filteredEvents = events.filter(event => {
    const matchesStatus =
      filterStatus === 'all' ||
      (filterStatus === 'active' && event.active) ||
      (filterStatus === 'inactive' && !event.active);
    const matchesSearch =
      !searchQuery ||
      event.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      event.description?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const getEventStatsForCard = (event) => {
    const s = eventStats[event.id];
    if (s && s.totalCapacity > 0) return s;
    let fallbackCapacity = 0;
    if (event.ticketTypes?.length > 0) {
      fallbackCapacity = event.ticketTypes.reduce((sum, tt) => sum + parseInt(tt.maxSupply || 0), 0);
    } else if (event.capacity) {
      fallbackCapacity = event.capacity;
    }
    return {
      ticketsSold: s?.ticketsSold || 0, revenue: s?.revenue || '0.00',
      totalCapacity: s?.totalCapacity || fallbackCapacity,
      available: s?.available || fallbackCapacity, percentSold: s?.percentSold || '0.00', attendees: 0,
    };
  };

  // ---------- Render ----------

  if (view === 'create' || view === 'edit') {
    return <EventForm event={selectedEvent} onSave={handleSaveEvent} onCancel={handleBackToList} saving={saving} saveStatus={saveStatus} />;
  }
  if (view === 'analytics') {
    return <EventAnalytics event={selectedEvent} onBack={handleBackToList} />;
  }
  if (view === 'license') {
    return (
      <div>
        <button className="btn-back" onClick={handleBackToList} style={{marginBottom: '20px'}}>← Back to Events</button>
        <LicensePanel />
      </div>
    );
  }
  if (view === 'support' && isSupportEnabled()) {
    return <div><SupportManagement onBack={handleBackToList} /></div>;
  }
  if (view === 'subadmins') {
    return (
      <div>
        <button className="btn-back" onClick={handleBackToList} style={{marginBottom: '20px'}}>← Back to Events</button>
        <SubadminPanel event={selectedEvent} onClose={handleBackToList} />
      </div>
    );
  }

  return (
    <div className="event-management">
      <div className="event-management-header">
        <div className="header-content">
          <h1>Event Management</h1>
          <p>On-chain events (Base Sepolia) — no server required</p>
        </div>
        <div className="header-actions">
          {isSupportEnabled() && (
            <button className="btn-license" onClick={() => setView('support')}>
              <span className="icon">🎧</span> Support
            </button>
          )}
          {LICENSE_ENABLED && (
            <button className="btn-license" onClick={() => setView('license')}>
              <span className="icon">🎫</span> License
            </button>
          )}
          <button className="btn-create-event" onClick={handleCreateEvent}>
            <span className="icon">➕</span> Create New Event
          </button>
        </div>
      </div>

      <div className="event-controls">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search events..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="filter-tabs">
          <button className={filterStatus === 'all' ? 'active' : ''} onClick={() => setFilterStatus('all')}>
            All Events ({events.length})
          </button>
          <button className={filterStatus === 'active' ? 'active' : ''} onClick={() => setFilterStatus('active')}>
            Active ({events.filter(e => e.active).length})
          </button>
          <button className={filterStatus === 'inactive' ? 'active' : ''} onClick={() => setFilterStatus('inactive')}>
            Inactive ({events.filter(e => !e.active).length})
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading events from blockchain...</p>
        </div>
      ) : filteredEvents.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📅</div>
          <h2>No Events Found</h2>
          <p>
            {searchQuery || filterStatus !== 'all'
              ? 'Try adjusting your search or filters'
              : 'Get started by creating your first on-chain event'}
          </p>
          {!searchQuery && filterStatus === 'all' && (
            <button className="btn-create-first" onClick={handleCreateEvent}>Create Your First Event</button>
          )}
        </div>
      ) : (
        <div className="events-grid">
          {filteredEvents.map((event) => {
            const stats = getEventStatsForCard(event);
            const eventDate = new Date(event.date || event.startTime * 1000);
            const isPast = eventDate < new Date();

            return (
              <div key={event.id} className={`event-card ${!event.active ? 'inactive' : ''}`}>
                <div className="event-card-header">
                  <div className="event-image">
                    <img
                      src={event.image || getDefaultImage(event.id)}
                      alt={event.name}
                      onError={(e) => {
                        const fallback = getDefaultImage(event.id);
                        if (!e.target.src.endsWith(fallback)) e.target.src = fallback;
                      }}
                    />
                    <div className="event-badge-container">
                      {event.active ? (
                        <span className="badge badge-active">Active</span>
                      ) : (
                        <span className="badge badge-inactive">Inactive</span>
                      )}
                      {isPast && <span className="badge badge-past">Past</span>}
                      {event._source && (
                        <span className={`badge badge-${event._source}`}>
                          {event._source === 'subgraph' ? 'Indexed' : event._source === 'contract' ? 'On-chain' : 'JSON'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="event-card-body">
                  <h3>{event.name}</h3>
                  <p className="event-description">{event.description}</p>

                  <div className="event-meta">
                    <div className="meta-item">
                      <span className="meta-icon">📅</span>
                      <span>{eventDate.toLocaleDateString()}</span>
                    </div>
                    <div className="meta-item">
                      <span className="meta-icon">⏰</span>
                      <span>{eventDate.toLocaleTimeString()}</span>
                    </div>
                    {(event.venue || event.location) && (
                      <div className="meta-item">
                        <span className="meta-icon">📍</span>
                        <span>{event.venue || event.location}</span>
                      </div>
                    )}
                    {event.capacity && (
                      <div className="meta-item">
                        <span className="meta-icon">👥</span>
                        <span>Capacity: {event.capacity}</span>
                      </div>
                    )}
                    <div className="meta-item">
                      <span className="meta-icon">🔗</span>
                      <span className="chain-id">Chain {event.chainId || CHAIN_ID}</span>
                    </div>
                  </div>

                  {(event.contractAddress || CONTRACT_ADDRESS) && (
                    <div className="event-contract">
                      <span className="contract-label">Contract:</span>
                      <span className="contract-address" title={event.contractAddress || CONTRACT_ADDRESS}>
                        {(event.contractAddress || CONTRACT_ADDRESS).slice(0, 6)}...{(event.contractAddress || CONTRACT_ADDRESS).slice(-4)}
                      </span>
                    </div>
                  )}

                  {event.ticketTypes && event.ticketTypes.length > 0 && (
                    <div className="event-ticket-types">
                      <span className="ticket-types-label">Ticket Types ({event.ticketTypes.length}):</span>
                      <div className="ticket-types-list">
                        {event.ticketTypes.slice(0, 3).map((tt, idx) => (
                          <span key={idx} className="ticket-type-tag">
                            {tt.name} - {(Number(tt.price) / 100).toFixed(2)} PGT
                          </span>
                        ))}
                        {event.ticketTypes.length > 3 && (
                          <span className="ticket-type-more">+{event.ticketTypes.length - 3} more</span>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="event-stats-mini">
                    <div className="stat-mini">
                      <div className="stat-value">{stats.ticketsSold}/{stats.totalCapacity}</div>
                      <div className="stat-label">Sold/Capacity</div>
                    </div>
                    <div className="stat-mini">
                      <div className="stat-value">{stats.revenue} PGT</div>
                      <div className="stat-label">Revenue</div>
                    </div>
                    <div className="stat-mini">
                      <div className="stat-value">{stats.available}</div>
                      <div className="stat-label">Available</div>
                    </div>
                  </div>
                </div>

                <div className="event-card-actions">
                  <button className="btn-action btn-analytics" onClick={() => handleViewAnalytics(event)}>
                    📊 Analytics
                  </button>
                  <button className="btn-action btn-subadmins" onClick={() => handleManageSubadmins(event)}>
                    👥 Managers
                  </button>
                  <button className="btn-action btn-edit" onClick={() => handleEditEvent(event)}>
                    ✏️ Edit
                  </button>
                  <button className="btn-action btn-toggle" onClick={() => handleToggleActive(event)}>
                    {event.active ? '⏸️ Deactivate' : '▶️ Activate'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EventManagement;
